import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  fr: {
    // Landing page
    heroTitle: "Votre Boutique Merch en",
    heroTitleHighlight: "30 Secondes",
    heroSubtitle: "Transformez votre profil Instagram ou site web en une boutique de merchandising. Aucune compétence design requise.",
    tabInstagram: "Instagram",
    tabWebsite: "Site Web",
    placeholderInstagram: "@votre_marque ou https://instagram.com/votre_marque",
    placeholderWebsite: "https://votreentreprise.com",
    generateButton: "Générer Ma Boutique",
    storesCreated: "boutiques créées",
    today: "aujourd'hui",
    trustedBy: "Utilisé par les grandes marques et créateurs",
    
    // How it works
    howItWorks: "Comment ça marche",
    howItWorksSubtitle: "De l'URL à la boutique en 3 étapes simples",
    step1Title: "Entrez votre URL",
    step1Desc: "Collez l'URL de votre profil Instagram ou site web",
    step2Title: "L'IA analyse votre marque",
    step2Desc: "Notre IA extrait automatiquement couleurs, style et identité",
    step3Title: "Obtenez votre boutique",
    step3Desc: "Recevez une boutique complète avec 9 produits uniques",
    
    // CTA
    ctaTitle: "Prêt à créer votre boutique merch ?",
    ctaSubtitle: "Rejoignez des milliers de créateurs et entreprises qui ont lancé leur merchandising avec InstaMerch",
    ctaButton: "Commencer gratuitement",
    
    // Loading
    loadingStep1: "Analyse de votre identité de marque...",
    loadingStep2: "Extraction de la palette de couleurs...",
    loadingStep3: "Génération des concepts de design...",
    loadingStep4: "Création de votre collection...",
    loadingStep5: "Finalisation de votre boutique...",
    loadingTime: "Habituellement 45-60 secondes",
    
    // Store preview
    storeReady: "Votre boutique est prête !",
    createNewStore: "Créer une nouvelle boutique",
    share: "Partager",
    startSelling: "Commencer à vendre",
    collectionsGenerated: "collections générées",
    detectedAs: "Nous avons détecté que vous êtes",
    chooseCollection: "Choisissez la collection qui vous correspond !",
    products: "produits",
    
    // Collections
    corporateEdition: "Corporate Edition",
    corporateDesc: "Pour vos événements & clients - Design professionnel avec logo",
    cultureEdition: "Team Culture Edition",
    cultureDesc: "Pour vos collaborateurs - Design fun avec phrases clés",
    artistCollection: "Artist Collection",
    artistDesc: "Designs inspirés du style artistique unique",
    
    // Products
    shopCollection: "Shop Collection",
    viewDetails: "Voir détails",
    contactToOrder: "Contacter pour commander",
    
    // Email popup
    emailTitle: "Vous aimez ce que vous voyez ?",
    emailTitleSuccess: "Vous êtes sur la liste !",
    emailSubtitle: "Soyez notifié quand les commandes seront ouvertes !",
    emailSubtitleSuccess: "Nous vous préviendrons dès l'ouverture des commandes.",
    notifyMe: "Me notifier",
    
    // CTA section
    readyToSell: "Prêt à commencer à vendre ?",
    readyToSellSubtitle: "Contactez-nous pour configurer votre boutique",
    requestChanges: "Demander des modifications",
    orderBoth: "Commander les 2 collections",
    
    // Footer
    poweredBy: "Propulsé par",
    terms: "Conditions",
    privacy: "Confidentialité",
    contact: "Contact",
  },
  en: {
    // Landing page
    heroTitle: "Your Merch Store in",
    heroTitleHighlight: "30 Seconds",
    heroSubtitle: "Transform your Instagram profile or website into a stunning merchandise store. No design skills needed.",
    tabInstagram: "Instagram",
    tabWebsite: "Website",
    placeholderInstagram: "@your_brand or https://instagram.com/your_brand",
    placeholderWebsite: "https://yourcompany.com",
    generateButton: "Generate My Merch Store",
    storesCreated: "stores created",
    today: "today",
    trustedBy: "Trusted by leading brands and creators",
    
    // How it works
    howItWorks: "How It Works",
    howItWorksSubtitle: "From URL to merchandise store in three simple steps",
    step1Title: "Enter Your URL",
    step1Desc: "Paste your Instagram profile or company website URL",
    step2Title: "AI Analyzes Your Brand",
    step2Desc: "Our AI extracts colors, style, and brand identity automatically",
    step3Title: "Get Your Store",
    step3Desc: "Receive a complete merchandise store with 9 unique products",
    
    // CTA
    ctaTitle: "Ready to Create Your Merch Store?",
    ctaSubtitle: "Join thousands of creators and businesses who have launched their merchandise with InstaMerch",
    ctaButton: "Get Started Free",
    
    // Loading
    loadingStep1: "Analyzing your brand identity...",
    loadingStep2: "Extracting color palette and visual style...",
    loadingStep3: "Generating unique design concepts...",
    loadingStep4: "Creating your merchandise collection...",
    loadingStep5: "Almost there, finalizing your store...",
    loadingTime: "Usually takes 45-60 seconds",
    
    // Store preview
    storeReady: "Your store is ready!",
    createNewStore: "Create New Store",
    share: "Share",
    startSelling: "Start Selling",
    collectionsGenerated: "collections generated",
    detectedAs: "We detected that you are a",
    chooseCollection: "Choose the collection that fits you!",
    products: "products",
    
    // Collections
    corporateEdition: "Corporate Edition",
    corporateDesc: "For your events & clients - Professional design with logo",
    cultureEdition: "Team Culture Edition",
    cultureDesc: "For your team - Fun designs with key phrases",
    artistCollection: "Artist Collection",
    artistDesc: "Designs inspired by unique artistic style",
    
    // Products
    shopCollection: "Shop Collection",
    viewDetails: "View Details",
    contactToOrder: "Contact to Order",
    
    // Email popup
    emailTitle: "Love what you see?",
    emailTitleSuccess: "You're on the list!",
    emailSubtitle: "Get notified when ordering goes live!",
    emailSubtitleSuccess: "We'll notify you when ordering goes live.",
    notifyMe: "Notify Me",
    
    // CTA section
    readyToSell: "Ready to Start Selling?",
    readyToSellSubtitle: "Contact us to set up your merchandise store",
    requestChanges: "Request Custom Changes",
    orderBoth: "Order Both Collections",
    
    // Footer
    poweredBy: "Powered by",
    terms: "Terms",
    privacy: "Privacy",
    contact: "Contact",
  }
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    // Check localStorage or browser language
    const saved = localStorage.getItem('instamerch-lang');
    if (saved) return saved;
    const browserLang = navigator.language.split('-')[0];
    return browserLang === 'fr' ? 'fr' : 'en';
  });

  useEffect(() => {
    localStorage.setItem('instamerch-lang', language);
  }, [language]);

  const t = (key) => translations[language][key] || translations['en'][key] || key;

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export function LanguageSwitcher() {
  const { language, setLanguage } = useLanguage();

  return (
    <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1" data-testid="language-switcher">
      <button
        onClick={() => setLanguage('fr')}
        className={`px-3 py-1 text-sm font-medium rounded-full transition-colors ${
          language === 'fr' 
            ? 'bg-black text-white' 
            : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        FR
      </button>
      <button
        onClick={() => setLanguage('en')}
        className={`px-3 py-1 text-sm font-medium rounded-full transition-colors ${
          language === 'en' 
            ? 'bg-black text-white' 
            : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        EN
      </button>
    </div>
  );
}
